import React, { useCallback } from 'react';
import {
  getAtonStyle,
  getVirtualAtonStyle,
  latLonToShape,
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeClosed } from 'react-icons/pi';
import { noop } from 'lodash';
import { AtoNDefinitionWithDate } from '@ais-ui/api';

interface EnabledShowAtonButtonProps {
  aton: AtoNDefinitionWithDate;
}

export const EnabledShowAtonButton = ({ aton }: EnabledShowAtonButtonProps) => {
  const { addLayer, doesLayerExist, zoomToLayer, removeLayerById } =
    useLayers();
  const { shapeToVectorLayer } = useOpenLayersContext();

  // Derive isApplied from the actual layer state instead of local state
  const isApplied = doesLayerExist(aton.uuid as string);

  const onApply = useCallback(() => {
    if (doesLayerExist(aton.uuid as string)) {
      removeLayerById(aton.uuid as string);
      return;
    }

    if (!aton?.position?.coordinates) {
      return;
    }

    const longitude = aton.position.coordinates[0];
    const latitude = aton.position.coordinates[1];

    const shape = latLonToShape(latitude, longitude);

    const layer = shapeToVectorLayer(shape);

    if (aton.virtual === 0) {
      layer.setStyle(getAtonStyle());
    } else {
      layer.setStyle(getVirtualAtonStyle());
    }

    addLayer(
      layer,
      aton.name as string,
      true,
      true,
      false,
      shape.properties,
      aton.uuid as string,
    );
    zoomToLayer(layer);
  }, [
    aton,
    shapeToVectorLayer,
    addLayer,
    doesLayerExist,
    removeLayerById,
    zoomToLayer,
  ]);

  return (
    <ShowAtonButton disabled={false} isApplied={isApplied} onApply={onApply} />
  );
};

interface ShowAtonButtonProps {
  onApply: () => void;
  isApplied: boolean;
  disabled: boolean;
}

export const DisabledShowAtonButton = () => {
  return <ShowAtonButton disabled={true} isApplied={false} onApply={noop} />;
};

export const ShowAtonButton = ({
  onApply,
  isApplied,
  disabled,
}: ShowAtonButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id="show-hide-aton-button"
          size="smIcon"
          variant="ghost"
          onClick={onApply}
          disabled={disabled}
        >
          {isApplied ? (
            <PiEye className="size-4" />
          ) : (
            <PiEyeClosed className="size-4"></PiEyeClosed>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{isApplied ? 'Hide AtoN' : 'Show AtoN'}</TooltipContent>
    </Tooltip>
  );
};
